import clsx from 'clsx';
import { addMonths, format, subMonths } from 'date-fns';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/Button';
import { IconButton } from '@/components/ui/IconButton';
import { Segmented } from '@/components/ui/Segmented';
import { useAppStore } from '@/app/store';
import { getDateFnsLocale, parseDateKey, todayKey, yearMonthKey } from '@/domain/dates';
import { partnerOf } from '@/domain/helpers';
import type { PlanFilters } from '@/domain/types';
import styles from './CalendarToolbar.module.css';

type FilterChipId = 'all' | 'shared' | 'only_me' | 'mine' | 'partner';

function resolveActiveChip(filters: PlanFilters, userId: string, partnerId?: string): FilterChipId {
  if (filters.visibility === 'shared' && filters.owners === 'all') return 'shared';
  if (filters.visibility === 'only_me' && filters.owners === 'all') return 'only_me';
  if (
    filters.owners !== 'all' &&
    filters.owners.length === 1 &&
    filters.owners[0] === userId &&
    filters.visibility === 'all'
  ) {
    return 'mine';
  }
  if (
    partnerId &&
    filters.owners !== 'all' &&
    filters.owners.length === 1 &&
    filters.owners[0] === partnerId &&
    filters.visibility === 'all'
  ) {
    return 'partner';
  }
  return 'all';
}

export function CalendarToolbar() {
  const { t, i18n } = useTranslation();
  const session = useAppStore((s) => s.session);
  const profiles = useAppStore((s) => s.profiles);
  const preferences = useAppStore((s) => s.preferences);
  const cursorMonth = useAppStore((s) => s.cursorMonth);
  const viewMode = useAppStore((s) => s.viewMode);
  const filters = useAppStore((s) => s.filters);
  const setCursorMonth = useAppStore((s) => s.setCursorMonth);
  const setSelectedDate = useAppStore((s) => s.setSelectedDate);
  const setViewMode = useAppStore((s) => s.setViewMode);
  const setFilters = useAppStore((s) => s.setFilters);

  const locale = preferences?.locale ?? (i18n.language.startsWith('ru') ? 'ru' : 'en');
  const dateFnsLocale = getDateFnsLocale(locale);
  const userId = session?.user.id ?? '';
  const partner = session ? partnerOf(profiles, session.user.id) : null;

  const monthDate = useMemo(() => parseDateKey(`${cursorMonth}-01`), [cursorMonth]);
  const monthTitle = format(monthDate, 'LLLL yyyy', { locale: dateFnsLocale });

  const activeChip = resolveActiveChip(filters, userId, partner?.id);

  const filterChips: { id: FilterChipId; label: string }[] = [
    { id: 'all', label: t('app.filters.all') },
    { id: 'shared', label: t('app.filters.shared') },
    { id: 'only_me', label: t('app.filters.onlyMe') },
    { id: 'mine', label: t('app.filters.mine') },
    { id: 'partner', label: t('app.filters.partner') },
  ];

  const applyFilter = (chip: FilterChipId) => {
    switch (chip) {
      case 'all':
        setFilters({ owners: 'all', visibility: 'all' });
        break;
      case 'shared':
        setFilters({ owners: 'all', visibility: 'shared' });
        break;
      case 'only_me':
        setFilters({ owners: 'all', visibility: 'only_me' });
        break;
      case 'mine':
        setFilters({ owners: userId ? [userId] : 'all', visibility: 'all' });
        break;
      case 'partner':
        setFilters({ owners: partner ? [partner.id] : 'all', visibility: 'all' });
        break;
    }
  };

  const goPrev = () => {
    setCursorMonth(format(subMonths(monthDate, 1), 'yyyy-MM'));
  };

  const goNext = () => {
    setCursorMonth(format(addMonths(monthDate, 1), 'yyyy-MM'));
  };

  const goToday = () => {
    const today = todayKey();
    setCursorMonth(yearMonthKey(new Date()));
    setSelectedDate(today);
  };

  return (
    <div className={styles.toolbar}>
      <div className={styles.rowNav}>
        <div className={styles.nav}>
          <IconButton label={t('common.back')} onClick={goPrev}>
            <ChevronLeft size={16} />
          </IconButton>
          <h2 className={styles.title}>{monthTitle}</h2>
          <IconButton label={t('common.next')} onClick={goNext}>
            <ChevronRight size={16} />
          </IconButton>
        </div>
      </div>

      <div className={styles.rowActions}>
        <Button
          variant="ghost"
          size="sm"
          className={styles.todayBtn}
          onClick={goToday}
        >
          {t('common.today')}
        </Button>
        <Segmented
          options={[
            { value: 'month', label: t('app.views.month') },
            { value: 'list', label: t('app.views.list') },
          ]}
          value={viewMode}
          onChange={setViewMode}
        />
      </div>

      <div className={styles.filters} role="group" aria-label={t('app.filters.all')}>
        {filterChips.map((chip) => (
          <button
            key={chip.id}
            type="button"
            className={clsx(styles.filterChip, activeChip === chip.id && styles.filterChipActive)}
            aria-pressed={activeChip === chip.id}
            onClick={() => applyFilter(chip.id)}
          >
            {chip.label}
          </button>
        ))}
      </div>
    </div>
  );
}
